/**
 * Energy Equation Solver — Step 3 of SIMPLE.
 *
 * Solves for enthalpy h at each cell center:
 *
 *   ρ · V · (h - h^n) / dt + Σ(ṁ_f · h_f) - V · (P - P^n) / dt = q_wall · A_wall + q_wall_inertia
 *
 * When pipe wall thermal mass is present (wallThickness > 0, wallRhoCp > 0):
 *   Wall energy equation (explicit, per cell):
 *     (ρcp)_wall · V_wall · (T_wall - T_wall^n) / dt = h_conv · A_inner · (T_fluid - T_wall)
 *   Fluid receives: q_wall_inertia = h_conv · A_inner · (T_wall - T_fluid)
 *
 * Uses upwind differencing for the convective term (ṁ_f · h_f).
 * Solved as a tridiagonal system via TDMA.
 */

import type { PipeSegment } from '../types';
import { tdma } from './linearSolver';

/**
 * Estimate convective heat transfer coefficient for liquid water in a pipe.
 * Dittus-Boelter: Nu = 0.023 × Re^0.8 × Pr^0.4
 * Simplified for water at ~20°C: k ≈ 0.6 W/(m·K), Pr ≈ 7
 */
function wallConvectionCoeff(rho: number, u: number, D: number, mu: number): number {
  const Re = rho * Math.abs(u) * D / Math.max(mu, 1e-6);
  if (Re < 100) return 200; // Natural convection floor
  const k_water = 0.6; // W/(m·K) — liquid water thermal conductivity
  const Pr = 7;         // Prandtl number for water ~20°C
  const Nu = 0.023 * Math.pow(Re, 0.8) * Math.pow(Pr, 0.4);
  return Nu * k_water / D;
}

/**
 * Solve energy equation for a single pipe.
 *
 * @param pipe The pipe segment
 * @param dt Time step [s]
 * @param alpha_h Enthalpy under-relaxation factor (0-1)
 * @returns Maximum enthalpy residual
 */
export function energyStep(
  pipe: PipeSegment,
  dt: number,
  alpha_h: number = 0.8,
): number {
  const { nCells, dx, area, wallHeatFlux, diameter } = pipe;
  const n = nCells;

  if (n === 0) return 0;

  const V = area * dx; // Cell volume [m³]
  const A_wall = Math.PI * diameter * dx; // Inner wall area per cell [m²]

  // --- Wall thermal inertia (explicit step, before fluid solve) ---
  const hasWallMass = (pipe.wallThickness ?? 0) > 0 && (pipe.wallRhoCp ?? 0) > 0;
  const wallHeatToFluid = new Array(n).fill(0); // W per cell

  if (hasWallMass && pipe.T_wall && pipe.T_wall_old) {
    const t_w = pipe.wallThickness!;
    const rhoCp_w = pipe.wallRhoCp!;
    // Wall volume per cell: annular ring π × D × t_w × dx (thin-wall approximation)
    const V_wall = Math.PI * diameter * t_w * dx;
    const C_wall = rhoCp_w * V_wall; // J/K — wall thermal capacitance per cell

    for (let i = 0; i < n; i++) {
      // Save old wall temperature
      pipe.T_wall_old[i] = pipe.T_wall[i];

      const T_fluid = pipe.cells[i].T;
      const T_w = pipe.T_wall[i];

      // Convective HTC from Dittus-Boelter
      const u_avg = (Math.abs(pipe.faces[i].u) + Math.abs(pipe.faces[i + 1].u)) / 2;
      const h_conv = wallConvectionCoeff(pipe.cells[i].rho, u_avg, diameter, pipe.cells[i].mu);

      // Heat flux from fluid to wall: q = h_conv × A_inner × (T_fluid - T_wall)
      const Q_fluid_to_wall = h_conv * A_wall * (T_fluid - T_w);

      // Update wall temperature (explicit Euler)
      pipe.T_wall[i] = T_w + Q_fluid_to_wall / C_wall * dt;

      // Heat from wall to fluid (equal and opposite, conservation)
      wallHeatToFluid[i] = -Q_fluid_to_wall; // W — positive if wall is hotter than fluid
    }
  }

  // --- Build tridiagonal system for h ---
  const a_sub = new Array(n).fill(0);
  const a_main = new Array(n).fill(0);
  const a_sup = new Array(n).fill(0);
  const rhs = new Array(n).fill(0);

  for (let i = 0; i < n; i++) {
    const cell = pipe.cells[i];
    const cell_old = pipe.cells_old[i];
    const mdot_in = pipe.faces[i].mdot;
    const mdot_out = pipe.faces[i + 1].mdot;

    // Temporal term: ρ · V / dt
    const a_t = cell.rho * V / dt;

    // Upwind convective fluxes
    const mdot_L = mdot_in;
    const mdot_R = mdot_out;

    // Contribution from left face
    const a_L = Math.max(mdot_L, 0);   // coefficient for h[i-1] if flow is from left
    const a_L_self = Math.max(-mdot_L, 0); // coefficient for h[i] if flow is to left

    // Contribution from right face
    const a_R = Math.max(-mdot_R, 0);  // coefficient for h[i+1] if flow is from right
    const a_R_self = Math.max(mdot_R, 0); // coefficient for h[i] if flow is to right

    // Sub-diagonal: coefficient for h[i-1]
    if (i > 0) {
      a_sub[i] = -a_L;
    }

    // Super-diagonal: coefficient for h[i+1]
    if (i < n - 1) {
      a_sup[i] = -a_R;
    }

    // Main diagonal
    a_main[i] = a_t + a_L_self + a_R_self;

    // RHS
    rhs[i] = a_t * cell_old.h                    // temporal: ρ·V/dt · h^n
            + V * (cell.P - cell_old.P) / dt      // pressure work: V·dP/dt
            + wallHeatFlux * A_wall               // external wall heat flux
            + wallHeatToFluid[i];                 // wall thermal inertia contribution

    // Boundary contributions — upwind enthalpy from boundary when flow enters the domain.
    if (i === 0 && mdot_L > 0) {
      rhs[i] += a_L * cell_old.h;
    }
    if (i === n - 1 && mdot_R < 0) {
      rhs[i] += a_R * cell_old.h;
    }
  }

  // Solve for h
  const h_new = tdma(a_sub, a_main, a_sup, rhs);

  // Apply with under-relaxation and compute residual (skip NaN).
  // Enthalpy range covers subcooled liquid (~1 kJ/kg at 0°C) through
  // superheated steam (~4200 kJ/kg at 800°C, 100 MPa). Use 0–5e6 J/kg.
  let maxResidual = 0;
  for (let i = 0; i < n; i++) {
    if (!isFinite(h_new[i])) continue;
    const h_clamped = Math.max(100, Math.min(5e6, h_new[i]));
    const h_old = pipe.cells[i].h;
    pipe.cells[i].h = h_old + alpha_h * (h_clamped - h_old);
    maxResidual = Math.max(maxResidual, Math.abs(h_clamped - h_old));
  }

  return maxResidual;
}
